import { Typography } from '@mui/material'
import React, { Fragment, memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getDriver } from 'src/selectors/driver'
import { getIsPartnerAccessRestricted, getIsPartnerFetchPending } from 'src/selectors/partner'
import tw from 'twin.macro'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { FetchPaymentMethod } from 'src/constants/actionTypes'
import { showToast } from 'src/utils/toast'
import { InfoContainer } from 'src/components/shared/InfoContainer'
import { ChevronLeftMinor } from '@shopify/polaris-icons'
import { Button } from 'src/components/shared/Button'
import { ContractorPortalSecureWrapper } from 'src/components/layout/contractorPortal/ContractorPortalSecureWrapper'
import { Navigate, useNavigate } from 'react-router-dom'
import { CONTRACTOR_PORTAL_ROUTES } from 'src/constants/routes'
import { Footer } from 'src/components/shared/Footer'
import { DriverPaymentMethod } from 'src/interfaces/paymentMethod'
import { CommonModal } from 'src/components/shared/CommonModal'
import SuccessImage from 'src/assets/images/success.png'
import FailedImage from 'src/assets/images/failed.png'
import { ContractorPortalVerifyPhoneModal } from 'src/components/contractor-portal/contractor-portal-verify-phone-modal/ContractorPortalVerifyPhoneModal'
import { ContractorPortalBankPaymentMethodForm } from 'src/components/contractor-portal/contractor-portal-bank-payment-method-form/ContractorPortalBankPaymentMethodForm'
import { ContractorPortalUsBankPaymentMethodDisplay } from 'src/components/contractor-portal/contractor-portal-payment-method-display/ContractorPortalUsBankPaymentMethodDisplay'

const ContractorPortalPaymentPageComponent = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const driver = useSelector(getDriver)
  const isPartnerAccessRestricted = useSelector(getIsPartnerAccessRestricted)
  const isPartnerFetchPending = useSelector(getIsPartnerFetchPending)

  const [verificationToken, setVerificationToken] = useState<string | null>(null)
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false)
  const [isFailedModalOpen, setIsFailedModalOpen] = useState<boolean>(false)
  const [paymentMethodInfo, setPaymentMethodInfo] = useState<DriverPaymentMethod | null>(null)
  const [isVerifyPhoneModalOpen, setIsVerifyPhoneModalOpen] = useState<boolean>(false)
  const [isBankPaymentFormShown, setIsBankPaymentFormShown] = useState<boolean>(false)

  const openVerifyPhoneModal = useCallback(() => {
    setIsVerifyPhoneModalOpen(true)
  }, [])

  const closeVerifyPhoneModal = useCallback(() => {
    setIsVerifyPhoneModalOpen(false)
  }, [])

  const closeFailedModal = useCallback(() => {
    setIsFailedModalOpen(false)
  }, [])

  const closeSuccessModal = useCallback(() => {
    setIsSuccessModalOpen(false)
  }, [])

  const handleBack = useCallback(() => {
    navigate(CONTRACTOR_PORTAL_ROUTES.HOME_URL)
  }, [navigate])

  const fetchPaymentMethod = useCallback(async () => {
    try {
      const paymentMethod: DriverPaymentMethod = await createAsyncAction(
        dispatch,
        FetchPaymentMethod.request({ flow: 'contractorPortal' }),
      )
      setPaymentMethodInfo(paymentMethod)
    } catch (error) {
      showToast('Failed to fetch settlement method', { variant: 'error' })
      setPaymentMethodInfo(null)
    }
  }, [dispatch])

  const handleBankPaymentSuccess = useCallback(() => {
    setIsSuccessModalOpen(true)
    setIsBankPaymentFormShown(false)

    setTimeout(() => {
      fetchPaymentMethod()
      setVerificationToken(null)
    }, 1000)
  }, [fetchPaymentMethod])

  const handleSuccessVerifyPhone = useCallback((token: string) => {
    setIsVerifyPhoneModalOpen(false)
    setVerificationToken(token)
    setIsBankPaymentFormShown(true)
  }, [])

  useEffect(() => {
    if (driver && !isPartnerAccessRestricted) {
      fetchPaymentMethod()
    }
  }, [driver, isPartnerAccessRestricted, fetchPaymentMethod])

  const paymentMethodDescription = useMemo(() => {
    if (paymentMethodInfo) {
      return 'To update your settlement method, tap the button below.'
    }

    return ''
  }, [paymentMethodInfo])

  const shouldShowUpdateButton = useMemo(() => {
    if (isBankPaymentFormShown) {
      return false
    }

    if (!paymentMethodInfo) {
      return false
    }

    return true
  }, [isBankPaymentFormShown, paymentMethodInfo])

  if (!driver || isPartnerFetchPending) {
    return <div />
  }

  if (isPartnerAccessRestricted) {
    return <Navigate to={CONTRACTOR_PORTAL_ROUTES.HOME_URL} replace />
  }

  return (
    <ContractorPortalSecureWrapper>
      <div css={tw`h-full flex w-full flex-col px-4 pt-20 sm:pt-6 pb-12`}>
        <div>
          <Button
            css={tw`bg-primary-100 h-[42px] w-[82px] hover:bg-primary-200`}
            color="light"
            size="medium"
            leftIcon={<ChevronLeftMinor width={19} css={tw`fill-primary-500`} />}
            onClick={
              isBankPaymentFormShown
                ? () => {
                    setIsBankPaymentFormShown(false)
                    setVerificationToken(null)
                  }
                : handleBack
            }
            data-testid="payments-header-back-btn"
          >
            Back
          </Button>
          {isBankPaymentFormShown ? (
            <div css={tw`mt-4`}>
              <ContractorPortalBankPaymentMethodForm
                handleFailure={() => setIsFailedModalOpen(true)}
                handleSuccess={handleBankPaymentSuccess}
                paymentMethodData={paymentMethodInfo}
                verificationToken={verificationToken}
              />
            </div>
          ) : (
            <Fragment>
              <Typography css={tw`mb-2 mt-4`} variant="h2">
                Settlement Method
              </Typography>
              <Typography css={tw`mb-4 text-shades-400`}>{paymentMethodDescription}</Typography>
              {paymentMethodInfo && (
                <InfoContainer dataTestId="contractors-list-payment-partners-notice-info">
                  Note: Your updated settlement method will also apply to all connected partners (if
                  any).
                </InfoContainer>
              )}
              <ContractorPortalUsBankPaymentMethodDisplay paymentMethod={paymentMethodInfo} />
            </Fragment>
          )}
        </div>
        {shouldShowUpdateButton && (
          <div css={tw`mt-auto`}>
            <Button
              className={tw`w-full h-[56px]`}
              onClick={openVerifyPhoneModal}
              disabled={isBankPaymentFormShown}
              data-testid="payments-update-method-btn"
            >
              Update settlement method
            </Button>
          </div>
        )}
      </div>
      {isVerifyPhoneModalOpen && (
        <ContractorPortalVerifyPhoneModal
          onClose={closeVerifyPhoneModal}
          onSuccess={handleSuccessVerifyPhone}
        />
      )}
      <CommonModal
        isOpen={isSuccessModalOpen}
        className={tw`max-w-[400px]`}
        onClose={closeSuccessModal}
        closeButtonTestId="contractors-list-payment-update-success-modal-close-btn"
      >
        <img css={tw`mb-2 w-full`} src={SuccessImage} />
        <Typography css={tw`mb-2 text-center`} variant="h2">
          Settlement Method Successfully Updated
        </Typography>
        <Typography css={tw`mb-6 text-shades-400 text-center`}>
          Your new settlement method is now active and will be used for future transactions.
        </Typography>
      </CommonModal>
      <CommonModal
        isOpen={isFailedModalOpen}
        className={tw`max-w-[400px]`}
        onClose={closeFailedModal}
        closeButtonTestId="contractors-list-payment-update-failed-modal-close-btn"
      >
        <img css={tw`mb-2 w-full`} src={FailedImage} />
        <Typography css={tw`mb-2 text-center`} variant="h2">
          Something Went Wrong
        </Typography>
        <Typography css={tw`mb-6 text-shades-400 text-center`}>
          Please try again later or contact support.
        </Typography>
      </CommonModal>
      <Footer
        className={tw`flex mt-auto items-center px-4 pt-2 pb-8 bg-shades-50 justify-center w-full`}
      />
    </ContractorPortalSecureWrapper>
  )
}

export const ContractorPortalPaymentPage = memo(ContractorPortalPaymentPageComponent)
